// R74 — THE FRONT DOOR'S WHOLE WELCOME VANISHED WHEN THE HOUSE COULD NOT READ ITS OWN CONFIG.
//
// `/api/featured-readings` is what the home page's hero is BUILT from: her logo's band, the eyebrow,
// the title, the subtext, the Ask Lala box a signed-out visitor types their first question into, and
// the nine featured readings. The read was `r.ok ? r.json() : null` → `if (d?.ok) setConfig(d)` →
// `.catch(() => {})`, and the component's first line is `if (!config?.featured?.length) return null`.
//
// **SO A FAILED READ DID NOT DRAW A SMALLER HERO. IT DREW NO HERO AT ALL** — the visitor lands on
// the front page of this site and meets the room cards with no welcome above them and nowhere to ask
// anything. That is R73's finding on the money page, one page earlier in the same journey, and it
// arrives the same way: **the failure path writes nothing, and `useState(null)` had already said
// "nothing".** The forgery does not need a literal.
//
// **AND THE SERVER WAS TELLING THE SAME LIE, WHICH IS THE SEVENTH ROUTE FOUND DOING IT.**
// `READING_BUBBLES` is parsed from `server/data/featured-readings.json` at boot inside a `try`, and
// the catch leaves the module-level fallback `{ featured: [], categories: [], readings: [] }`
// standing. `handleReadingBubblesList` then answered **`200 { ok: true, featured: [], eyebrow: "",
// title: "" }`** — R64/R70's ruling exactly: the `ok: true` makes it WORSE than silence, because a
// client that trusts `ok` is told the owner has featured nothing when the truth is the house never
// read its own file. **The flag is the LOAD, never the LENGTH:** an owner who empties `featured` has
// chosen no showcase and that is a real answer; a boot that could not parse the file has no answer
// at all, and only the boot knows which happened.
//
// **THE ANSWER IS `Config | null`, NOT THE FOUR-WAY ONE, AND IT IS ARGUED (R58's ruling).** This
// route is PUBLIC by design — its own comment says *"the LIST is public (a signed-out visitor must
// see the showcase)"* — so it reads no session and can never answer 401. A `sign-in` branch would be
// a second answer key for something the route has no way of saying.
//
// `doFetch` is a PARAMETER (R46), so both answers are reachable with no browser and no server.

// **THE PAYLOAD'S SHAPE, BESIDE THE TRANSPORT THAT READS IT** (R73's currency): these two types
// described `/api/featured-readings`'s body and sat 400 lines from the only fetch of it.
export type ShowcaseBubble = {
  key: string;
  category: string;
  categoryLabel: string;
  tint: string;
  icon: string;
  question: string;
  secondPerson?: boolean;
  secondPersonOptional?: boolean;
  // AKASHIC-1: a card that names a room is a door rather than a question.
  room?: string;
  reading?: string;
};

export type ShowcaseConfig = {
  eyebrow: string;
  title: string;
  subtext: string;
  searchPlaceholder: string;
  featuredHeading: string;
  askHeading?: string;
  askChips?: string[];
  askLine?: string;
  categories: Array<{ key: string; label: string; cardLabel: string; tint: string; icon: string }>;
  featured: ShowcaseBubble[];
  all: ShowcaseBubble[];
};

export const showcasePath = "/api/featured-readings";

// `null` is "we could not read the showcase". It is deliberately NOT an empty showcase: an EMPTY
// `featured` from a landed read is the owner's own choice and the caller still honours it, which is
// the whole distinction the server's boot flag exists to preserve (R18).
export async function readShowcase(
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<ShowcaseConfig | null> {
  let response: Response;
  try {
    response = await doFetch(showcasePath);
  } catch {
    return null;
  }
  const data = await response.json().catch(() => null) as (Partial<ShowcaseConfig> & { ok?: boolean }) | null;
  // PRESENCE, never truthiness (R53), AND the server's own `ok: false` (R70): the FEATURED list is
  // what makes this a showcase, so a 200 carrying no array is a read that did not land — and a body
  // that says `ok: false` is the house telling us it could not read its own file, whatever rode with it.
  if (!response.ok || !data || data.ok === false || !Array.isArray(data.featured)) return null;
  return data as ShowcaseConfig;
}

// **THREE VALUES, BECAUSE TWO CANNOT SAY IT** (W5-2/R39's third state, ITEM 7's `self` argument):
// `undefined` is "the answer has not come back", `null` is "it came back and we could not read the
// showcase", and a showcase is a showcase. Only the middle one has anything to say — a front page
// that flashes a failure at every visitor while its own first request is in flight is worse than the
// gap it is apologising for.
export function showcaseNote(answer: ShowcaseConfig | null | undefined, failed: string) {
  return answer === null ? failed : "";
}
